import { cn } from "@/lib/cn";

export interface VitalSigns {
  /** SLA countdown / freshness label rendered on the left. We don't
   *  ship a real SLA clock yet (Phase 8); the field is populated
   *  with the last_poll_at relative label so the user can see how
   *  fresh the cockpit is. */
  sla_label: string;
  queue_depth: number;
  overdue: number;
  due_today: number;
  escalation_level: "ok" | "active" | "critical";
}

interface RibbonProps {
  vitals: VitalSigns;
}

const ESCALATION_LIGHT_CLASSES: Record<VitalSigns["escalation_level"], string> = {
  ok: "bg-op-green",
  active: "bg-op-amber",
  critical: "bg-bucket-need-reply-now",
};

const ESCALATION_LABEL: Record<VitalSigns["escalation_level"], string> = {
  ok: "OK",
  active: "Active",
  critical: "Level 1",
};

/** Top of the cockpit. Five vital signs the operator wants to see
 *  before reading any card: SLA freshness, queue depth, due today,
 *  overdue, and the escalation light. Stays mounted across screens so
 *  the operator never loses context.
 */
export function VitalSignsRibbon({ vitals }: RibbonProps) {
  return (
    <div
      data-testid="vital-signs-ribbon"
      className="flex flex-wrap items-center gap-4 border-b border-op-border bg-op-surface px-4 py-3 md:gap-8 md:px-6"
    >
      <Stat
        label="SLA"
        value={vitals.sla_label}
        accent="text-op-green"
        data-testid="vital-sla"
      />
      <Stat
        label="Queue depth"
        value={String(vitals.queue_depth)}
        data-testid="vital-queue-depth"
      />
      <Stat
        label="Due today"
        value={String(vitals.due_today)}
        accent="text-op-amber"
        data-testid="vital-due-today"
      />
      <Stat
        label="Overdue"
        value={String(vitals.overdue)}
        accent={
          vitals.overdue > 0
            ? "text-bucket-need-reply-now"
            : "text-op-secondary"
        }
        data-testid="vital-overdue"
      />
      <div
        data-testid="vital-escalation"
        className="ml-auto flex items-center gap-2 rounded border border-op-border px-2 py-1"
      >
        <span
          aria-hidden
          className={cn(
            "inline-block h-2.5 w-2.5 rounded-full",
            ESCALATION_LIGHT_CLASSES[vitals.escalation_level],
          )}
        />
        <span className="text-[10px] uppercase tracking-wider text-op-secondary">
          Escalation
        </span>
        <span
          className={cn(
            "font-mono text-xs",
            vitals.escalation_level === "ok"
              ? "text-op-secondary"
              : "text-op-primary",
          )}
        >
          {ESCALATION_LABEL[vitals.escalation_level]}
        </span>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  ...rest
}: {
  label: string;
  value: string;
  accent?: string;
  "data-testid"?: string;
}) {
  return (
    <div className="flex flex-col" {...rest}>
      <span className="text-[10px] uppercase tracking-wider text-op-muted">
        {label}
      </span>
      <span
        className={cn(
          "font-mono text-lg leading-none text-op-primary",
          accent,
        )}
      >
        {value}
      </span>
    </div>
  );
}
